import { type Href, useRouter } from 'expo-router';
import { type ReactElement, type ReactNode, useState } from 'react';
import ActionButton from '@/components/UI/ActionButton';
import { SectionHeading } from '@/components/UI/Section';
import Text from '@/components/UI/Text';
import Box from '@/components/Unistyles/Box';
import { graphql, type ResultOf } from '@/gql/graphql';
import type useDebounce from '@/hooks/useDebounce';
import type usePaginatedList from '@/hooks/usePaginatedList';
import {
  useSelectionClear,
  useSelectionSelectedIds,
  useSelectionStore,
  useSelectionToggle,
} from '@/hooks/useSelection';
import OrganizationAssignmentDashboard from '@/screens/OrganizationAssignmentDashboard/OrganizationAssignmentDashboard';
import {
  getOrganizationMemberAssignmentColumns,
  toOrganizationAssignmentRow,
} from '@/screens/OrganizationAssignmentDashboard/OrganizationAssignmentDashboard.utils';

export const GetPersonBadgeAssignments = graphql(/* GraphQL */ `
  query GetPersonBadgeAssignments(
    $where: badge_assignee_bool_exp!
    $limit: Int!
    $offset: Int!
  ) {
    badge_assignee(
      where: $where
      limit: $limit
      offset: $offset
      order_by: [{ updated_at: desc }, { id: asc }]
    ) {
      id
      event_id
      team_id
      created_at
      updated_at
      can_assign
      badge {
        id
        title
        color
      }
      event {
        id
        title
      }
      team {
        id
        title
      }
      person {
        id
        name
        color
        avatar_id
      }
      badge_assignee_attributes(order_by: { badge_attribute: { order: asc } }) {
        id
        badge_attribute_id
        value
        formatted_value
        badge_attribute {
          id
          title
        }
      }
    }
    badge_assignee_aggregate(where: $where) {
      aggregate {
        count
      }
    }
  }
`);

export function PersonBadgeAssignments({
  personId,
  data,
  search,
  pagination,
  editHref,
  deleteHref,
  headerActions,
}: {
  headerActions?: ReactNode;
  editHref?: (
    assignmentId: string,
    eventId: string | null,
    teamId: string | null,
  ) => Href;
  deleteHref?: (assignmentIds: string[]) => Href;
  personId: string;
  data: ResultOf<typeof GetPersonBadgeAssignments>;
  search: ReturnType<typeof useDebounce>;
  pagination: ReturnType<typeof usePaginatedList>;
}) {
  const { push } = useRouter();
  const selectionStore = useSelectionStore<string>();
  const clearSelection = useSelectionClear(selectionStore);
  const selectedIds = useSelectionSelectedIds(selectionStore);
  const toggleSelection = useSelectionToggle(selectionStore);
  const [selecting, setSelecting] = useState(false);

  const rows = data.badge_assignee.map(toOrganizationAssignmentRow);
  const showDashboard =
    rows.length > 0 ||
    (data.badge_assignee_aggregate.aggregate?.count ?? 0) > 0 ||
    Boolean(search.value || search.debouncedValue) ||
    pagination.offset > 0;
  const editableIds = data.badge_assignee
    .filter((assignment) => assignment.can_assign)
    .map((assignment) => assignment.id);
  const selectedEditableIds = selectedIds.filter((id) =>
    editableIds.includes(id),
  );
  const resetSelection = () => {
    clearSelection();
    setSelecting(false);
  };
  const columns = getOrganizationMemberAssignmentColumns({
    assignmentCount: editableIds.length,
    canSelectAssignments: selecting,
    selectedBadgeAttributes: [],
    testIDPrefix: 'PersonDetail',
    selectionStore,
    visibleAssignmentIds: editableIds,
  }).map((column) => {
    if (column.id !== 'selected') return column;

    return {
      ...column,
      renderCell: (row: (typeof rows)[number]) => {
        if (!editableIds.includes(row.assignmentId)) return <Box />;
        return column.renderCell?.(row) ?? <Box />;
      },
    };
  });

  let actionContent: ReactElement | null = null;
  if (selecting || selectedEditableIds.length > 0) {
    actionContent = (
      <Box direction="horizontal" space="md" wrap>
        <ActionButton
          forceLabel
          label="Done"
          variant="secondary"
          icon="close-outline"
          onPress={resetSelection}
        />
        {selectedEditableIds.length > 0 && (
          <ActionButton
            forceLabel
            label={`Delete (${selectedEditableIds.length})`}
            icon="trash-outline"
            variant="error"
            onPress={() => {
              push(
                deleteHref?.(selectedEditableIds) ?? {
                  pathname: '/badge-assignee/[assigneeId]/delete',
                  params: {
                    assigneeId: selectedEditableIds[0],
                    assigneeIds: selectedEditableIds.join(','),
                    assignmentCount: String(selectedEditableIds.length),
                  },
                },
              );
              resetSelection();
            }}
          />
        )}
      </Box>
    );
  } else if (editableIds.length > 0) {
    actionContent = (
      <ActionButton
        forceLabel
        label="Select"
        variant="secondary"
        icon="checkbox-outline"
        onPress={() => setSelecting(true)}
      />
    );
  }

  return (
    <Box space="md">
      <SectionHeading
        title="Badges"
        actionPlacement="inline"
        actions={
          <Box direction="horizontal" align="center" space="md" wrap>
            {headerActions}
            {actionContent}
          </Box>
        }
      />
      {showDashboard ? (
        <OrganizationAssignmentDashboard
          fetching={false}
          assignmentCount={data.badge_assignee_aggregate.aggregate?.count ?? 0}
          columns={columns}
          rows={rows}
          paginated={{
            ...pagination,
            nextPage: () => {
              resetSelection();
              return pagination.nextPage();
            },
            previousPage: () => {
              resetSelection();
              return pagination.previousPage();
            },
          }}
          searchValue={search.value}
          searchPlaceholder="Search Badges..."
          onChangeText={(value) => {
            resetSelection();
            pagination.resetPage();
            search.onChangeText(value);
          }}
          onPressCancelSearch={() => {
            resetSelection();
            pagination.resetPage();
            search.reset();
          }}
          emptyMessage={
            search.value ? 'No matching badges' : 'No badges to show'
          }
          onPressRow={
            editableIds.length
              ? (row) => {
                  if (!editableIds.includes(row.assignmentId)) return;
                  if (selecting) {
                    toggleSelection(
                      row.assignmentId,
                      !selectedEditableIds.includes(row.assignmentId),
                    );
                    return;
                  }
                  push(
                    editHref?.(row.assignmentId, row.eventId, row.teamId) ?? {
                      pathname: '/badge-assignee/[assigneeId]/edit',
                      params: {
                        assigneeId: row.assignmentId,
                        personId,
                        eventId: row.eventId ?? undefined,
                        teamId: row.teamId ?? undefined,
                      },
                    },
                  );
                }
              : undefined
          }
        />
      ) : (
        <Text size="sm" variant="typographySecondary">
          No badges assigned.
        </Text>
      )}
    </Box>
  );
}
